import React, { useEffect, useRef, useState, useMemo } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import NavBar from "../components/layout/NavBar";
import OuterContainer from "../components/layout/OuterContainer";
import RoundedImage from "../components/RoundedImage";
import Loader from "../components/Loading";
import unknownAvatar from "../assets/unknown_gw.png";
import { request } from "../services/networking/request";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import { User as UserIcon, Camera, Shield, Plus, X, CheckCircle, Bell, MapPin, Hash, Trash2, Edit3, AlertTriangle, Link } from "lucide-react";
import { signout } from "../store/actions";
import { GoogleLogin } from "@react-oauth/google";
import InstagramLogo from "../assets/instagram.svg";
import FacebookLogo from "../assets/facebook.svg";
import LinkedinLogo from "../assets/linkedin.svg";
import YelpLogo from "../assets/yelp.svg";
import GoogleBusinessLogo from "../assets/google.svg";

/* ============ Styled Components (styling only) ============ */

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  // padding-top: 64px;
`;

const Header = styled.div`
  text-align: center;
  margin: 24px 0 16px;

  /* Adjust margins for mobile */
  @media screen and (max-width: 768px) {
    margin: 32px 0 20px;
  }

  @media screen and (max-width: 480px) {
    margin: 24px 0 16px;
  }
`;

const Title = styled.h1`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 28px;
  font-weight: 600;
  letter-spacing: 0;
  color: #0f172a;
  margin: 0;
  text-shadow: none;

  svg {
    color: #3b82f6;
  }

  /* Responsive font sizes for mobile */
  @media screen and (max-width: 768px) {
    font-size: 24px;
    gap: 6px;
  }

  @media screen and (max-width: 480px) {
    font-size: 22px;
    gap: 6px;
    letter-spacing: 0px;
  }
`;

const Subtitle = styled.p`
  margin: 6px 0 16px;
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;

  /* Responsive font size for mobile */
  @media screen and (max-width: 768px) {
    font-size: 15px;
    margin: 8px 0 24px;
  }

  @media screen and (max-width: 480px) {
    font-size: 14px;
    margin: 6px 0 20px;
  }
`;

const TabNav = styled.div`
  display: flex;
  gap: 6px;
  background: #ffffff;
  border-radius: 10px;
  padding: 4px;
  border: 1px solid #e2e8f0;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;

  @media (max-width: 640px) {
    width: 100%;
    max-width: 100%;
  }
`;

const TabButton = styled.button`
  flex: 1 1 0;
  min-width: 0;
  background: ${(props) => (props.active ? "#eef2ff" : "transparent")};
  border: 1px solid ${(props) => (props.active ? "#e0e7ff" : "transparent")};
  border-radius: 8px;
  padding: 6px 10px;
  font-weight: 600;
  font-size: 0.82rem;
  color: ${(props) => (props.active ? "#3b82f6" : "#64748b")};
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  box-shadow: none;
  white-space: nowrap;
  text-align: center;

  &:hover {
    color: #3b82f6;
    background: #eef2ff;
  }

  @media (max-width: 640px) {
    padding: 8px 4px;
    font-size: 0.75rem;
  }

  @media (max-width: 480px) {
    font-size: 0.65rem;
    padding: 8px 2px;
    white-space: normal;
    line-height: 1.2;
  }
`;

const CardsWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  margin: 0 0 44px;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  width: 100%;
  max-width: 760px;
  border: 1px solid #e2e8f0;
  box-shadow: none;
  transition: border-color 0.2s ease;

  @media (max-width: 768px) {
    padding: 24px;
    border-radius: 12px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 16px;
  color: #0f172a;
  letter-spacing: -0.02em;
`;

const ProfileFlex = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  div:first-child {
    letter-spacing: 0.2px;
  }
`;

const ChangePhotoButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 6px 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease;

  svg {
    color: #0f172a;
    min-width: 16px;
  }

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 18px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #475569;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  
  /* Make asterisks red for required fields */
  & span[style*="color: red"], 
  & > *[style*="color: red"] {
    color: #ef4444 !important;
  }
  
  /* Target asterisks specifically */
  &::after {
    content: "";
  }
  
  /* Style asterisks that appear after the label text */
  :global(*) {
    color: inherit;
  }
`;

const RequiredAsterisk = styled.span`
  color: #ef4444;
  margin-left: 2px;
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13.5px;
  color: #0f172a;
  width: 100%;
  background: #ffffff;
  transition: box-shadow 120ms ease, border-color 120ms ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: #93c5fd;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.12);
  }
`;

const TextArea = styled.textarea`
  padding: 9px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13.5px;
  color: #0f172a;
  width: 100%;
  resize: vertical;
  min-height: 96px;
  transition: box-shadow 120ms ease, border-color 120ms ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: #93c5fd;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.12);
  }
`;

const BioSection = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 6px;
`;

const PortfolioSection = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
`;

const PortfolioControls = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: 160px 1fr;
  width: 100%;
  flex: 1;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const IconField = styled.div`
  position: relative;
  width: 100%;

  svg,
  img {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    pointer-events: none;
  }

  input,
  select {
    padding-left: 36px;
  }
`;

const BrandIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

const HelperText = styled.p`
  margin: 0;
  font-size: 11.5px;
  color: #64748b;
  line-height: 1.4;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TagPill = styled.span`
  font-size: 11.5px;
  color: #475569;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 4px 8px;
`;

const PortfolioRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;

  input {
    flex: 1;
  }

  @media (max-width: 720px) {
    align-items: stretch;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  color: #059669;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease, color 120ms ease;

  &:hover {
    background: #f8fafc;
    border-color: #059669;
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #ffffff;
  color: #dc2626;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease, color 120ms ease;

  &:hover {
    background: #fef2f2;
    border-color: #dc2626;
  }
`;

const SaveButton = styled.button`
  margin-top: 16px;
  padding: 9px 16px;
  background: #3b82f6;
  color: #ffffff;
  border: 1px solid #3b82f6;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  float: right;
  box-shadow: none;
  transition: background 120ms ease, opacity 120ms ease;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(248, 250, 252, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
`;

/* ===== Security / Password UI ===== */

const PwRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
`;

const PwField = styled.div`
  display: grid;
  gap: 6px;
  position: relative;
`;

const ToggleBtn = styled.button`
  position: absolute;
  right: 8px;
  top: 34px;
  transform: translateY(-50%);
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 8px;
  font-size: 12px;
  padding: 6px 8px;
  color: #475569;
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
  }
`;

const SmallNote = styled.div`
  font-size: 11.5px;
  color: #64748b;
`;

const EmailVerifiedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  
  svg {
    color: #10b981;
    min-width: 16px;
    min-height: 16px;
  }
`;

const EmailInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-wrap: wrap;
`;

const ErrorText = styled.div`
  color: #dc2626;
  font-size: 12px;
  font-weight: 700;
`;

const SuccessText = styled.div`
  color: #16a34a;
  font-size: 13px;
  font-weight: 800;
`;

const BarWrap = styled.div`
  height: 8px;
  border-radius: 999px;
  background: #eef2f7;
  overflow: hidden;
`;

const Bar = styled.div`
  height: 100%;
  width: ${({ pct }) => pct}%;
  background: ${({ color }) => color};
  transition: width 160ms ease;
  border-radius: 999px;
`;

const Skeleton = styled.div`
  border-radius: 12px;
  background: linear-gradient(90deg, #f1f5f9, #f8fafc, #f1f5f9);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite linear;
  height: ${(p) => p.h || "14px"};
  width: ${(p) => p.w || "100%"};

  @keyframes shimmer {
    from {
      background-position: 200% 0;
    }
    to {
      background-position: -200% 0;
    }
  }
`;

/* ===== Preferences UI ===== */

const PreferencesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PreferenceCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  position: relative;
  transition: border-color 0.2s ease;
  
  &:hover {
    border-color: #cbd5e1;
  }
`;

const PreferenceHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const PreferenceName = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PreferenceActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${props => props.danger ? '#fee2e2' : '#e2e8f0'};
  border-radius: 6px;
  background: ${props => props.danger ? '#fef2f2' : '#ffffff'};
  color: ${props => props.danger ? '#dc2626' : '#475569'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.danger ? '#fef2f2' : '#f8fafc'};
    border-color: ${props => props.danger ? '#dc2626' : '#94a3b8'};
  }
`;

const PreferenceDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
`;

const PreferenceTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
`;

const TagGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 9999;
  padding: 64px 16px 16px 16px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.14);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalContent = styled.div`
  padding: 16px;
  overflow-y: auto;
  flex: 1;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    
    &:hover {
      background: #94a3b8;
    }
  }
  
  /* For Firefox */
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
`;

const Select = styled.select`
  width: 100%;
  max-width: 100%;
  padding: 9px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13.5px;
  background: white;
  color: #374151;
  transition: all 0.2s ease;
  box-sizing: border-box;
  min-width: 0;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.08);
  }

  &:hover {
    border-color: #9ca3af;
  }

  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;
  padding-right: 40px;
`;

const CheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
  max-height: 180px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: #f8fafc;
  }
  
  input[type="checkbox"] {
    margin: 0;
  }
`;

const TagInputContainer = styled.div`
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 12px;
  min-height: 40px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  cursor: text;
  background: white;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.08);
  }

  &:hover {
    border-color: #9ca3af;
  }
`;

const KeywordTag = styled.div`
  display: flex;
  align-items: center;
  background: #eef2ff;
  color: #1d4ed8;
  padding: 4px 8px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  gap: 4px;
  
  button {
    background: none;
    border: none;
    color: #1d4ed8;
    cursor: pointer;
    padding: 0;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    
    &:hover {
      background: rgba(59, 130, 246, 0.1);
    }
  }
`;

const TagInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  flex: 1;
  min-width: 120px;
  font-size: 13.5px;
  color: #374151;
  
  &::placeholder {
    color: #9ca3af;
  }
`;

/* ===== Blocked Status Banner ===== */

const BlockedBanner = styled.div`
  background: #fff7f7;
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 18px;
  margin: 0 0 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: none;

  svg {
    color: #dc2626;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    padding: 20px;
    margin: 0 0 24px;
  }
`;

const BlockedContent = styled.div`
  flex: 1;
`;

const BlockedTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 1.05rem;
  font-weight: 600;
  color: #dc2626;
`;

const BlockedText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #7f1d1d;
  line-height: 1.6;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ModalFooter = styled.div`
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
  flex-shrink: 0;
  background: white;
`;

const ModalButton = styled.button`
  padding: 7px 14px;
  border-radius: 6px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.primary ? `
    background: #3b82f6;
    color: white;
    border: 1px solid #3b82f6;
    
    &:hover {
      background: #2563eb;
    }
  ` : `
    background: white;
    color: #475569;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
  
  svg {
    margin-bottom: 16px;
    color: #94a3b8;
  }
  
  h3 {
    margin: 0 0 8px;
    font-size: 16px;
    font-weight: 600;
    color: #475569;
  }
  
  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
  }
`;


/* ============ Component (logic + new Security tab) ============ */

const AccountSettings = ({ state, dispatch }) => {
  const profileTypeOptions = [
    { value: "yelp", label: "Yelp" },
    { value: "facebook", label: "Facebook" },
    { value: "instagram", label: "Instagram" },
    { value: "google_business", label: "Google Business Profile" },
    { value: "linkedin", label: "LinkedIn" },
    { value: "other", label: "Other" },
  ];
  const [activeTab, setActiveTab] = useState("profile");
  const [user, setUser] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [portfolioErrors, setPortfolioErrors] = useState([]);

  const getProfileIcon = (type) => {
    switch (type) {
      case "instagram":
        return { src: InstagramLogo, alt: "Instagram" };
      case "facebook":
        return { src: FacebookLogo, alt: "Facebook" };
      case "linkedin":
        return { src: LinkedinLogo, alt: "LinkedIn" };
      case "google_business":
        return { src: GoogleBusinessLogo, alt: "Google Business Profile" };
      case "yelp":
        return { src: YelpLogo, alt: "Yelp" };
      default:
        return null;
    }
  };
  const fileInput = useRef(null);

  // Password form state
  const [pw, setPw] = useState({
    current: "",
    next: "",
    confirm: "",
  });
  const [pwVisible, setPwVisible] = useState({
    current: false,
    next: false,
    confirm: false,
  });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");

  // Delete account state
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Notification preferences state
  const [notificationPreferences, setNotificationPreferences] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showPreferenceModal, setShowPreferenceModal] = useState(false);
  const [editingPreference, setEditingPreference] = useState(null);
  const [savingPreference, setSavingPreference] = useState(false);
  const [preferenceForm, setPreferenceForm] = useState({
    name: "",
    description: "",
    task_type: "",
    min_budget: "",
    max_budget: "",
    location_type: "any",
    zipcode: "",
    radius: 25,
    keywords: [],
    categories: [],
    max_notifications_per_day: 10
  });

  // Delete state
  const [deletingPreference, setDeletingPreference] = useState(null);
  
  // Show advanced options state
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  // Current keyword input
  const [currentKeyword, setCurrentKeyword] = useState('');
  const [keywordError, setKeywordError] = useState('');

  useEffect(() => {
    setLoading(true);
    request("POST", API_CONSTANT_MAP.get_account_info)
      .then((res) => {
        const d = res.data.data;
        const portfolioLinks = Array.isArray(d.portfolio_links)
          ? d.portfolio_links
          : [];
        const normalizedPortfolio =
          portfolioLinks.length > 0
            ? portfolioLinks.map((item) =>
                typeof item === "string"
                  ? { type: "other", url: item }
                  : {
                      type: item?.type || "other",
                      url: item?.url || "",
                    }
              )
            : [{ type: "other", url: "" }];
        setUser(d);
        setForm({
          first_name: d.first_name || "",
          last_name: d.last_name || "",
          phone_number: d.phone_number || "",
          zipcode: d.zipcode || "",
          website: d.website || "",
          bio: d.bio || "",
          portfolio: normalizedPortfolio,
          avatar: d.avatar || "",
          email_verified: d.email_verified !== undefined ? d.email_verified : true,
        });
      })
      .finally(() => setLoading(false));
    
    // Load notification preferences on component mount for tab count
    loadNotificationPreferences();
  }, []);

  // Check URL parameter for tab on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam === 'preferences' || tabParam === 'notifications') {
      setActiveTab('preferences');
    } else if (tabParam === 'security') {
      setActiveTab('security');
    }
  }, []);

  // Load categories and preferences when preferences tab becomes active
  useEffect(() => {
    if (activeTab === "preferences") {
      loadCategories();
      loadNotificationPreferences();
    }
  }, [activeTab]);

  const onChange = (e) => {
    const { id, value } = e.target;
    setForm((f) => ({ ...f, [id]: value }));
  };

  const validatePortfolioItem = (item) => {
    if (!item?.url) return "";
    if (!/^https?:\/\//i.test(item.url)) {
      return "Please use a full URL (https://...)";
    }
    return "";
  };

  const onPortfolioChange = (i, field, val) => {
    setForm((f) => {
      const p = [...f.portfolio];
      p[i] = { ...p[i], [field]: val };
      return { ...f, portfolio: p };
    });
    setPortfolioErrors((errs) => {
      const next = [...errs];
      const item = { ...(form?.portfolio?.[i] || {}), [field]: val };
      next[i] = validatePortfolioItem(item);
      return next;
    });
  };

  const addPortfolio = () => {
    setForm((f) => ({
      ...f,
      portfolio: [...f.portfolio, { type: "other", url: "" }],
    }));
    setPortfolioErrors((errs) => [...errs, ""]);
  };

  const removePortfolio = (index) => {
    setForm((f) => {
      const next = f.portfolio.filter((_, i) => i !== index);
      return {
        ...f,
        portfolio:
          next.length > 0 ? next : [{ type: "other", url: "" }],
      };
    });
    setPortfolioErrors((errs) => {
      const next = errs.filter((_, i) => i !== index);
      return next.length > 0 ? next : [""];
    });
  };

  const onAvatarClick = () => fileInput.current.click();

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Only allow web-compatible image formats
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a JPEG, PNG, GIF, or WebP image.');
      return;
    }

    const preview = URL.createObjectURL(file);
    setForm((f) => ({ ...f, avatar: preview }));

    const initUrl = API_CONSTANT_MAP.get_presigned_upload_url;
    if (!initUrl || typeof initUrl !== "string") {
      console.error("upload_avatar_init is not configured:", initUrl);
      alert("Avatar upload endpoint is not configured.");
      return;
    }

    let initResp;
    try {
      initResp = await request("POST", initUrl, {
        filename: file.name,
        content_type: file.type,
      });
    } catch (err) {
      console.error("Upload init failed:", err);
      alert(err?.error?.message || "Failed to start avatar upload.");
      return;
    }

    const presignedUrl = initResp?.data?.data?.presigned_url;
    const publicUrl = initResp?.data?.data?.image_url;

    if (!presignedUrl || !publicUrl) {
      console.error("Bad init response shape:", initResp?.data);
      alert("Server did not return an upload URL.");
      return;
    }

    try {
      const put = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!put.ok) {
        throw new Error(`S3 upload failed (${put.status})`);
      }
    } catch (err) {
      console.error("S3 PUT failed:", err);
      alert(err.message || "S3 upload failed.");
      return;
    }

    setForm((f) => ({ ...f, avatar: publicUrl }));
  };

  const saveProfile = async () => {
    const validationErrors = form?.portfolio?.map((item) =>
      validatePortfolioItem(item)
    );
    const hasValidationErrors = validationErrors?.some((err) => err);
    if (hasValidationErrors) {
      setPortfolioErrors(validationErrors);
      return;
    }
    setLoading(true);
    try {
      await request("POST", API_CONSTANT_MAP.update_account_details_v2, {
        first_name: form.first_name,
        last_name: form.last_name,
        phone_number: form.phone_number,
        zipcode: form.zipcode,
        website: form.website,
        bio: form.bio,
        portfolio_links: form.portfolio.map((item) => ({
          type: item.type,
          url: item.url,
        })),
        avatar: form.avatar,
      });
      console.log("Profile saved");
    } catch {
      console.log("Failed to save");
    } finally {
      setLoading(false);
    }
  };

  // ===== Password helpers =====
  const scorePassword = (s) => {
    if (!s) return 0;
    let score = 0;
    if (s.length >= 8) score += 1;
    if (/[A-Z]/.test(s)) score += 1;
    if (/[a-z]/.test(s)) score += 1;
    if (/\d/.test(s)) score += 1;
    if (/[^A-Za-z0-9]/.test(s)) score += 1;
    return Math.min(score, 5);
  };
  const strength = scorePassword(pw.next);
  const pct = [0, 25, 45, 65, 85, 100][strength];
  const color =
    strength <= 1
      ? "#ef4444"
      : strength === 2
      ? "#f59e0b"
      : strength === 3
      ? "#22c55e"
      : "#16a34a";

  const canSubmitPw =
    pw.current.length > 0 &&
    pw.next.length >= 8 &&
    pw.next === pw.confirm &&
    strength >= 2;

  const submitPassword = async () => {
    setPwError("");
    setPwSuccess("");
    if (!canSubmitPw) {
      setPwError(
        "Please fix the errors above before saving your new password."
      );
      return;
    }
    setPwSaving(true);
    try {
      // Endpoint fallback: prefer update_password, otherwise change_password
      const endpoint =
        API_CONSTANT_MAP.update_password || API_CONSTANT_MAP.change_password;

      if (!endpoint) {
        throw new Error(
          "Password endpoint not configured (update_password / change_password)."
        );
      }

      const { data } = await request("POST", endpoint, {
        current_password: pw.current,
        new_password: pw.next,
      });

      if (!data?.success) {
        const msg =
          data?.error?.message ||
          "Could not change password. Please try again.";
        throw new Error(msg);
      }

      setPwSuccess("✅ Password updated successfully.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      setPwError(err.message || "Failed to change password.");
    } finally {
      setPwSaving(false);
    }
  };

  // ===== Delete Account =====
  const isGoogleUser = user?.is_google_user;

  const handleDeleteAccount = async (googleToken) => {
    setDeleteError("");
    if (!isGoogleUser && !deletePassword) {
      setDeleteError("Please enter your password to confirm deletion.");
      return;
    }
    setDeleting(true);
    try {
      const payload = isGoogleUser
        ? { googleToken }
        : { password: deletePassword };
      const { data } = await request("POST", API_CONSTANT_MAP.delete_account, payload);
      if (!data?.success) {
        throw new Error(data?.error?.message || "Failed to delete account.");
      }
      dispatch(signout());
      window.location.href = "/";
    } catch (err) {
      setDeleteError(err?.error?.message || err.message || "Failed to delete account.");
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // ===== Notification Preferences helpers =====
  const loadCategories = async () => {
    try {
      const response = await request("POST", API_CONSTANT_MAP.fetch_categories);
      if (response?.data?.success) {
        setCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  };

  const loadNotificationPreferences = async () => {
    try {
      const response = await request("GET", API_CONSTANT_MAP.fetch_notification_preferences);
      if (response?.data?.success) {
        setNotificationPreferences(response.data.data || []);
      } else {
        setNotificationPreferences([]);
      }
    } catch (error) {
      console.error("Failed to load notification preferences:", error);
      setNotificationPreferences([]);
    }
  };

  const resetPreferenceForm = () => {
    setPreferenceForm({
      name: "",
      description: "",
      task_type: "",
      min_budget: "",
      max_budget: "",
      location_type: "any",
      zipcode: "",
      radius: 25,
      keywords: [],
      categories: [],
      max_notifications_per_day: 10
    });
  };

  const openPreferenceModal = (preference = null) => {
    // Ensure categories are loaded before opening modal
    if (categories.length === 0) {
      loadCategories();
    }
    
    if (preference) {
      setEditingPreference(preference);
      setPreferenceForm({
        name: preference.name || "",
        description: preference.description || "",
        task_type: preference.task_type || "",
        min_budget: preference.min_budget || "",
        max_budget: preference.max_budget || "",
        location_type: preference.location_type || "any",
        zipcode: preference.zipcode || "",
        radius: preference.radius || 25,
        keywords: Array.isArray(preference.keywords) ? preference.keywords : [],
        categories: preference.categories || [],
        max_notifications_per_day: preference.max_notifications_per_day || 10
      });
    } else {
      setEditingPreference(null);
      resetPreferenceForm();
    }
    setShowPreferenceModal(true);
  };

  const closePreferenceModal = () => {
    setShowPreferenceModal(false);
    setEditingPreference(null);
    resetPreferenceForm();
  };

  const handlePreferenceFormChange = (field, value) => {
    setPreferenceForm(prev => {
      let updates = { [field]: value };
      
      // Handle location type changes based on task type
      if (field === "task_type") {
        if (value === "online") {
          // If task type is remote, set location to remote and clear zipcode
          updates.location_type = "remote";
          updates.zipcode = "";
          updates.radius = 25;
        } else if (value === "in-person") {
          // If task type is in-person and location was remote, change to "any"
          if (prev.location_type === "remote") {
            updates.location_type = "any";
          }
        }
        // For "All tasks", keep current location_type as is
      }
      
      return {
        ...prev,
        ...updates
      };
    });
  };

  // Get available location options based on task type
  const getLocationOptions = (taskType) => {
    if (taskType === "online") {
      return [
        { value: "remote", label: "Remote" }
      ];
    } else if (taskType === "in-person") {
      return [
        { value: "any", label: "All" },
        { value: "zipcode", label: "Specific ZIP code area" }
      ];
    } else {
      // taskType === "" (All tasks) or any other value
      return [
        { value: "any", label: "All" },
        { value: "zipcode", label: "Specific ZIP code area" },
        { value: "remote", label: "Remote" }
      ];
    }
  };

  const toggleCategory = (categoryId) => {
    setPreferenceForm(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryId)
        ? prev.categories.filter(id => id !== categoryId)
        : [...prev.categories, categoryId]
    }));
  };

  const addKeyword = (keyword) => {
    if (!keyword || typeof keyword !== 'string') return;
    const trimmedKeyword = keyword.trim();
    if (trimmedKeyword.length === 0) return;
    
    // Clear any existing error
    setKeywordError('');
    
    // Validate 1-2 words only
    const wordCount = trimmedKeyword.split(/\s+/).length;
    if (wordCount > 2) {
      setKeywordError('Keywords should be 1 or 2 words maximum. Try "garage door" instead of "garage door installation".');
      return;
    }
    
    // Check for duplicates
    if (preferenceForm.keywords.includes(trimmedKeyword)) {
      setKeywordError('This keyword has already been added. Try a different variation like "doorbell" or "installation".');
      return;
    }
    
    setPreferenceForm(prev => ({
      ...prev,
      keywords: [...prev.keywords, trimmedKeyword]
    }));
    
    setCurrentKeyword('');
  };

  const removeKeyword = (keywordToRemove) => {
    setPreferenceForm(prev => ({
      ...prev,
      keywords: prev.keywords.filter(keyword => keyword !== keywordToRemove)
    }));
  };

  const savePreference = async () => {
    setSavingPreference(true);
    try {
      const keywords = Array.isArray(preferenceForm.keywords) 
        ? preferenceForm.keywords.filter(k => k.trim().length > 0)
        : [];

      const preferenceData = {
        ...preferenceForm,
        keywords,
        min_budget: preferenceForm.min_budget ? parseFloat(preferenceForm.min_budget) : null,
        max_budget: preferenceForm.max_budget ? parseFloat(preferenceForm.max_budget) : null,
        radius: parseInt(preferenceForm.radius)
      };

      let response;
      if (editingPreference) {
        // Update existing preference
        const endpoint = `${API_CONSTANT_MAP.update_notification_preference}/${editingPreference.id}`;
        response = await request("PUT", endpoint, preferenceData);
      } else {
        // Create new preference
        response = await request("POST", API_CONSTANT_MAP.create_notification_preference, preferenceData);
      }

      if (response?.data?.success) {
        closePreferenceModal();
        loadNotificationPreferences(); // Reload to show changes
      } else {
        console.error("Failed to save preference:", response?.data?.message || "Unknown error");
        alert(`Failed to ${editingPreference ? 'update' : 'create'} notification preference. Please try again.`);
      }
    } catch (error) {
      console.error("Failed to save preference:", error);
      alert(`Failed to ${editingPreference ? 'update' : 'create'} notification preference. Please try again.`);
    } finally {
      setSavingPreference(false);
    }
  };

  const deletePreference = async (preferenceId) => {
    setDeletingPreference(preferenceId);

    try {
      const endpoint = `${API_CONSTANT_MAP.delete_notification_preference}/${preferenceId}`;
      const response = await request("DELETE", endpoint);
      
      if (response?.data?.success) {
        loadNotificationPreferences(); // Reload to show changes
      } else {
        console.error("Failed to delete preference:", response?.data?.message || "Unknown error");
        alert("Failed to delete notification preference. Please try again.");
      }
    } catch (error) {
      console.error("Failed to delete preference:", error);
      alert("Failed to delete notification preference. Please try again.");
    } finally {
      setDeletingPreference(null);
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : `Category ${categoryId}`;
  };

  // Generate dynamic preview text based on current selections
  const previewText = useMemo(() => {
    const { categories: selectedCategories, keywords, zipcode, location_type } = preferenceForm;
    
    if (selectedCategories.length === 0 && keywords.length === 0) {
      return "Select categories or add keywords to see what you'll be notified about.";
    }

    let parts = [];
    
    // Categories part
    if (selectedCategories.length > 0) {
      const categoryNames = selectedCategories.map(id => getCategoryName(id));
      if (categoryNames.length === 1) {
        parts.push(`all ${categoryNames[0]} tasks`);
      } else if (categoryNames.length === 2) {
        parts.push(`all ${categoryNames[0]} and ${categoryNames[1]} tasks`);
      } else {
        parts.push(`all ${categoryNames.slice(0, -1).join(', ')}, and ${categoryNames[categoryNames.length - 1]} tasks`);
      }
    }

    // Keywords part
    if (keywords.length > 0) {
      if (keywords.length === 1) {
        parts.push(`any tasks mentioning "${keywords[0]}"`);
      } else if (keywords.length === 2) {
        parts.push(`any tasks mentioning "${keywords[0]}" or "${keywords[1]}"`);
      } else {
        parts.push(`any tasks mentioning "${keywords.slice(0, -1).join('", "')}", or "${keywords[keywords.length - 1]}"`);
      }
    }

    // Location part
    let locationText = "";
    if (location_type === "zipcode" && zipcode) {
      locationText = ` in ${zipcode}`;
    } else if (location_type === "remote") {
      locationText = " (remote tasks only)";
    } else {
      locationText = " in your area";
    }

    // Combine parts
    let result = "You'll be notified for ";
    if (parts.length === 1) {
      result += parts[0];
    } else {
      result += parts.join(" and ");
    }
    result += locationText + ".";

    return result;
  }, [preferenceForm.categories, preferenceForm.keywords, preferenceForm.zipcode, preferenceForm.location_type, categories]);

  if (!form) {
    return (
      <>
        <NavBar user={state.user} />
        <PageWrapper>
          <OuterContainer>
            <Header>
              <Title>Account Settings</Title>
              <Subtitle>Manage your profile and account preferences</Subtitle>
              <TabNav>
                <TabButton style={{ pointerEvents: "none" }}>
                  <Skeleton h="16px" w="60px" />
                </TabButton>
                <TabButton style={{ pointerEvents: "none" }}>
                  <Skeleton h="16px" w="70px" />
                </TabButton>
                <TabButton style={{ pointerEvents: "none" }}>
                  <Skeleton h="16px" w="90px" />
                </TabButton>
              </TabNav>
            </Header>

            <CardsWrapper>
              {/* Profile Picture Card Skeleton */}
              <Card>
                <CardHeader>
                  <Skeleton h="22px" w="22px" />
                  <Skeleton h="22px" w="120px" />
                </CardHeader>
                <ProfileFlex>
                  <Skeleton h="84px" w="84px" style={{ borderRadius: "50%" }} />
                  <ProfileInfo>
                    <Skeleton
                      h="20px"
                      w="150px"
                      style={{ marginBottom: "8px" }}
                    />
                    <Skeleton
                      h="16px"
                      w="180px"
                      style={{ marginBottom: "12px" }}
                    />
                    <Skeleton
                      h="36px"
                      w="120px"
                      style={{ borderRadius: "8px" }}
                    />
                  </ProfileInfo>
                </ProfileFlex>
              </Card>

              {/* Basic Information Card Skeleton */}
              <Card>
                <CardHeader>
                  <Skeleton h="22px" w="22px" />
                  <Skeleton h="22px" w="140px" />
                </CardHeader>
                <Grid>
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <FieldWrapper key={i}>
                      <Skeleton
                        h="16px"
                        w="80px"
                        style={{ marginBottom: "8px" }}
                      />
                      <Skeleton h="44px" w="100%" />
                    </FieldWrapper>
                  ))}
                  <BioSection>
                    <Skeleton
                      h="16px"
                      w="60px"
                      style={{ marginBottom: "8px" }}
                    />
                    <Skeleton h="100px" w="100%" />
                  </BioSection>
                  <PortfolioSection>
                    <Skeleton
                      h="16px"
                      w="140px"
                      style={{ marginBottom: "12px" }}
                    />
                    <PortfolioRow>
                      <Skeleton h="44px" w="140px" />
                      <Skeleton h="44px" w="100%" />
                      <Skeleton h="44px" w="42px" />
                    </PortfolioRow>
                    <PortfolioRow>
                      <Skeleton h="44px" w="140px" />
                      <Skeleton h="44px" w="100%" />
                      <Skeleton h="44px" w="42px" />
                    </PortfolioRow>
                  </PortfolioSection>
                </Grid>
                <Skeleton
                  h="44px"
                  w="120px"
                  style={{ marginTop: "26px", borderRadius: "8px" }}
                />
              </Card>
            </CardsWrapper>
          </OuterContainer>
        </PageWrapper>
      </>
    );
  }

  return (
    <>
      <NavBar user={state.user} />
      <PageWrapper>
        <OuterContainer>
          {/* Show blocked status banner if user is blocked */}
          {state?.user?.is_blocked && (
            <BlockedBanner>
              <Shield size={24} />
              <BlockedContent>
                <BlockedTitle>Account Suspended</BlockedTitle>
                <BlockedText>
                  Your account has been suspended. Contact support if you believe this is an error.
                </BlockedText>
              </BlockedContent>
            </BlockedBanner>
          )}
          
          <Header>
            <Title>Account Settings</Title>
            <Subtitle>Manage your profile and account preferences</Subtitle>
            <TabNav>
              <TabButton
                active={activeTab === "profile"}
                onClick={() => setActiveTab("profile")}
              >
                Profile
              </TabButton>
              <TabButton
                active={activeTab === "security"}
                onClick={() => setActiveTab("security")}
              >
                Security
              </TabButton>
              <TabButton
                active={activeTab === "preferences"}
                onClick={() => setActiveTab("preferences")}
              >
                Notifications {notificationPreferences.length > 0 && `(${notificationPreferences.length})`}
              </TabButton>
            </TabNav>
          </Header>

          {activeTab === "profile" && (
            <CardsWrapper>
              {loading && (
                <Overlay>
                  <Loader animating size={48} />
                </Overlay>
              )}

              <Card>
                <CardHeader>
                  <Camera size={22} /> Profile Picture
                </CardHeader>
                <ProfileFlex>
                  <RoundedImage
                    image={form.avatar || unknownAvatar}
                    imageWidth={84}
                    imageHeight={84}
                    borderRadiusPct={50}
                  />
                  <ProfileInfo>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 800,
                        color: "#0f172a",
                      }}
                    >
                      <span style={{ textTransform: "capitalize" }}>
                        {form.first_name} {form.last_name}
                      </span>
                    </div>
                    <EmailInfo style={{ fontSize: 14, color: "#64748b" }}>
                      <span>{user.email}</span>
                      {form.email_verified && (
                        <EmailVerifiedBadge title="Email Verified">
                          <CheckCircle size={16} />
                        </EmailVerifiedBadge>
                      )}
                    </EmailInfo>
                    <ChangePhotoButton onClick={onAvatarClick}>
                      <Camera size={16} /> Change Photo
                    </ChangePhotoButton>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      ref={fileInput}
                      onChange={onFileSelected}
                      style={{ display: "none" }}
                    />
                  </ProfileInfo>
                </ProfileFlex>
              </Card>

              <Card>
                <CardHeader>
                  <UserIcon size={22} /> Basic Information
                </CardHeader>
                <Grid>
                  <FieldWrapper>
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      value={form.first_name}
                      onChange={onChange}
                      placeholder="Your first name"
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      value={form.last_name}
                      onChange={onChange}
                      placeholder="Your last name"
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Label htmlFor="phone_number">Phone Number</Label>
                    <Input
                      id="phone_number"
                      value={form.phone_number}
                      onChange={onChange}
                      placeholder="+1 (555) 123-4567"
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Label htmlFor="zipcode">ZIP Code</Label>
                    <Input
                      id="zipcode"
                      value={form.zipcode}
                      onChange={onChange}
                      placeholder="e.g., 90210"
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      value={form.website}
                      onChange={onChange}
                      placeholder="https://yourwebsite.com"
                    />
                  </FieldWrapper>

                  <BioSection>
                    <Label htmlFor="bio">Bio</Label>
                    <TextArea
                      id="bio"
                      rows={3}
                      value={form.bio}
                      onChange={onChange}
                      placeholder="Tell us about yourself..."
                    />
                  </BioSection>

                  <PortfolioSection>
                    <Label>Business & Social Profiles</Label>
                    <HelperText>
                      Examples: instagram.com/yourbrand • yelp.com/biz/yourbrand • linkedin.com/in/yourname
                    </HelperText>
                    {(form.portfolio.length > 0
                      ? form.portfolio
                      : [{ type: "other", url: "" }]
                    ).map((item, i) => (
                        <React.Fragment key={i}>
                        <PortfolioRow>
                          <PortfolioControls>
                            <IconField>
                              {(() => {
                                const icon = getProfileIcon(item.type);
                                return icon ? (
                                  <BrandIcon src={icon.src} alt={icon.alt} />
                                ) : (
                                  <Link size={16} />
                                );
                              })()}
                              <Select
                                aria-label="Profile type"
                                value={item.type}
                                onChange={(e) =>
                                  onPortfolioChange(i, "type", e.target.value)
                                }
                              >
                                {profileTypeOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </Select>
                            </IconField>
                            <IconField>
                              <Link size={16} />
                              <Input
                                value={item.url}
                                onChange={(e) =>
                                  onPortfolioChange(i, "url", e.target.value)
                                }
                                placeholder="Paste a profile URL"
                              />
                            </IconField>
                          </PortfolioControls>
                          <RemoveButton
                            onClick={() => removePortfolio(i)}
                            title="Remove this profile link"
                          >
                            <X size={16} />
                          </RemoveButton>
                          {i === form.portfolio.length - 1 && (
                            <AddButton
                              onClick={addPortfolio}
                              title="Add another profile link"
                            >
                              <Plus size={16} />
                              </AddButton>
                          )}
                        </PortfolioRow>
                        {!!portfolioErrors?.[i] && (
                          <HelperText style={{ color: "#b91c1c" }}>
                            {portfolioErrors[i]}
                          </HelperText>
                        )}
                        </React.Fragment>
                      ))}
                  </PortfolioSection>
                </Grid>
                <SaveButton onClick={saveProfile}>Save Profile</SaveButton>
              </Card>
            </CardsWrapper>
          )}

          {activeTab === "security" && (
            <CardsWrapper>
              <Card>
                <CardHeader>
                  <Shield size={22} /> Change Password
                </CardHeader>

                <PwRow>
                  <PwField>
                    <Label htmlFor="current">Current Password</Label>
                    <Input
                      id="current"
                      type={pwVisible.current ? "text" : "password"}
                      value={pw.current}
                      onChange={(e) =>
                        setPw((p) => ({ ...p, current: e.target.value }))
                      }
                      placeholder="Enter current password"
                      autoComplete="current-password"
                    />
                    <ToggleBtn
                      type="button"
                      onClick={() =>
                        setPwVisible((v) => ({ ...v, current: !v.current }))
                      }
                    >
                      {pwVisible.current ? "Hide" : "Show"}
                    </ToggleBtn>
                  </PwField>

                  <PwField>
                    <Label htmlFor="next">New Password</Label>
                    <Input
                      id="next"
                      type={pwVisible.next ? "text" : "password"}
                      value={pw.next}
                      onChange={(e) => {
                        setPw((p) => ({ ...p, next: e.target.value }));
                        setPwError("");
                        setPwSuccess("");
                      }}
                      placeholder="At least 8 characters"
                      autoComplete="new-password"
                    />
                    <ToggleBtn
                      type="button"
                      onClick={() =>
                        setPwVisible((v) => ({ ...v, next: !v.next }))
                      }
                    >
                      {pwVisible.next ? "Hide" : "Show"}
                    </ToggleBtn>
                    <SmallNote>
                      Use a mix of upper & lower case, numbers, and symbols.
                    </SmallNote>
                    <BarWrap>
                      <Bar pct={pct} color={color} />
                    </BarWrap>
                  </PwField>

                  <PwField>
                    <Label htmlFor="confirm">Confirm New Password</Label>
                    <Input
                      id="confirm"
                      type={pwVisible.confirm ? "text" : "password"}
                      value={pw.confirm}
                      onChange={(e) =>
                        setPw((p) => ({ ...p, confirm: e.target.value }))
                      }
                      placeholder="Re-enter new password"
                      autoComplete="new-password"
                    />
                    <ToggleBtn
                      type="button"
                      onClick={() =>
                        setPwVisible((v) => ({ ...v, confirm: !v.confirm }))
                      }
                    >
                      {pwVisible.confirm ? "Hide" : "Show"}
                    </ToggleBtn>
                    {pw.confirm && pw.next !== pw.confirm && (
                      <ErrorText>New passwords do not match.</ErrorText>
                    )}
                  </PwField>

                  {pwError && <ErrorText>{pwError}</ErrorText>}
                  {pwSuccess && <SuccessText>{pwSuccess}</SuccessText>}

                  <SaveButton
                    style={{ float: "none", justifySelf: "end" }}
                    onClick={submitPassword}
                    disabled={!canSubmitPw || pwSaving}
                  >
                    {pwSaving ? "Saving…" : "Save New Password"}
                  </SaveButton>
                </PwRow>
              </Card>

              <Card style={{ borderColor: '#fecaca' }}>
                <CardHeader style={{ color: '#dc2626' }}>
                  <AlertTriangle size={22} /> Delete Account
                </CardHeader>

                <div style={{
                  background: '#fff7f7',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  lineHeight: '1.5',
                  color: '#991b1b'
                }}>
                  <strong>Warning:</strong> This action cannot be undone. Deleting your account will:
                  <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                    <li>Cancel all your open tasks</li>
                    <li>Withdraw all your pending offers</li>
                    <li>Remove your notification preferences</li>
                    <li>Anonymize your personal information</li>
                  </ul>
                </div>

                {isGoogleUser ? (
                  <>
                    <p style={{ fontSize: '13px', color: '#475569', marginBottom: '10px' }}>
                      Confirm with your Google account to delete.
                    </p>

                    {deleteError && <ErrorText style={{ marginBottom: '8px' }}>{deleteError}</ErrorText>}

                    {!showDeleteConfirm ? (
                      <SaveButton
                        style={{
                          float: 'none',
                          justifySelf: 'end',
                          background: '#dc2626',
                          borderColor: '#dc2626',
                          marginTop: '8px'
                        }}
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={deleting}
                      >
                        Delete My Account
                      </SaveButton>
                    ) : (
                      <div style={{
                        marginTop: '8px',
                        padding: '12px',
                        background: '#fff7f7',
                        borderRadius: '8px',
                        border: '1px solid #fecaca'
                      }}>
                        <p style={{ margin: '0 0 10px', fontWeight: 600, color: '#991b1b', fontSize: '13px' }}>
                          Are you sure? Click the Google button below to confirm deletion. This cannot be undone.
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                          <GoogleLogin
                            onSuccess={(credentialResponse) => handleDeleteAccount(credentialResponse.credential)}
                            onError={() => setDeleteError("Google verification failed. Please try again.")}
                            theme="outline"
                            size="large"
                            text="continue_with"
                          />
                          <SaveButton
                            style={{
                              float: 'none',
                              marginTop: '0',
                              background: '#64748b',
                              borderColor: '#64748b'
                            }}
                            onClick={() => setShowDeleteConfirm(false)}
                            disabled={deleting}
                          >
                            Cancel
                          </SaveButton>
                        </div>
                        {deleting && <p style={{ marginTop: '8px', fontSize: '12px', color: '#991b1b' }}>Deleting account...</p>}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <PwField>
                      <Label htmlFor="delete-pw">Enter your password to confirm</Label>
                      <Input
                        id="delete-pw"
                        type="password"
                        value={deletePassword}
                        onChange={(e) => {
                          setDeletePassword(e.target.value);
                          setDeleteError("");
                        }}
                        placeholder="Your current password"
                        autoComplete="current-password"
                      />
                    </PwField>

                    {deleteError && <ErrorText style={{ marginTop: '8px' }}>{deleteError}</ErrorText>}

                    {!showDeleteConfirm ? (
                      <SaveButton
                        style={{
                          float: 'none',
                          justifySelf: 'end',
                          background: '#dc2626',
                          borderColor: '#dc2626',
                          marginTop: '12px'
                        }}
                        onClick={() => {
                          if (!deletePassword) {
                            setDeleteError("Please enter your password to confirm deletion.");
                            return;
                          }
                          setShowDeleteConfirm(true);
                        }}
                        disabled={deleting}
                      >
                        Delete My Account
                      </SaveButton>
                    ) : (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        background: '#fff7f7',
                        borderRadius: '8px',
                        border: '1px solid #fecaca'
                      }}>
                        <p style={{ margin: '0 0 10px', fontWeight: 600, color: '#991b1b', fontSize: '13px' }}>
                          Are you sure? This cannot be undone.
                        </p>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <SaveButton
                            style={{
                              float: 'none',
                              marginTop: '0',
                              background: '#dc2626',
                              borderColor: '#dc2626',
                              flex: 1
                            }}
                            onClick={() => handleDeleteAccount()}
                            disabled={deleting}
                          >
                            {deleting ? "Deleting..." : "Yes, Delete My Account"}
                          </SaveButton>
                          <SaveButton
                            style={{
                              float: 'none',
                              marginTop: '0',
                              background: '#64748b',
                              borderColor: '#64748b',
                              flex: 1
                            }}
                            onClick={() => setShowDeleteConfirm(false)}
                            disabled={deleting}
                          >
                            Cancel
                          </SaveButton>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Card>
            </CardsWrapper>
          )}

          {activeTab === "preferences" && (
            <CardsWrapper>
              <Card>
                <CardHeader>
                  <Bell size={22} /> Notification Preferences
                </CardHeader>
                
                {notificationPreferences.length === 0 ? (
                  <EmptyState>
                    <Bell size={48} />
                    <h3>No Notification Preferences</h3>
                    <p>Create your first notification rule to get alerted when tasks matching your criteria are posted.</p>
                  </EmptyState>
                ) : (
                  <PreferencesGrid>
                    {notificationPreferences.map((preference) => (
                      <PreferenceCard key={preference.id}>
                        <PreferenceHeader>
                          <PreferenceName>
                            {preference.name}
                          </PreferenceName>
                          <PreferenceActions>
                            <ActionButton
                              onClick={() => openPreferenceModal(preference)}
                              title="Edit preference"
                            >
                              <Edit3 size={14} />
                            </ActionButton>
                            <ActionButton
                              danger
                              onClick={() => deletePreference(preference.id)}
                              disabled={deletingPreference === preference.id}
                              title={deletingPreference === preference.id ? "Deleting..." : "Delete preference"}
                            >
                              {deletingPreference === preference.id ? "Deleting..." : <Trash2 size={14} />}
                            </ActionButton>
                          </PreferenceActions>
                        </PreferenceHeader>
                        
                        <PreferenceDetails>
                          {/* Location */}
                          <div style={{ marginBottom: "8px" }}>
                            <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "500" }}>Location: </span>
                            <span style={{ fontSize: "13px", color: "#374151" }}>
                              {preference.location_type === "zipcode" && `${preference.zipcode} (${preference.radius}mi)`}
                              {preference.location_type === "remote" && "Remote only"}
                              {preference.location_type === "any" && "Any location"}
                            </span>
                          </div>

                          {/* Categories & Keywords in one flow */}
                          <div style={{ marginBottom: "8px" }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                              {preference.categories && preference.categories.length > 0 && (
                                <>
                                  {preference.categories.map(categoryId => (
                                    <PreferenceTag key={categoryId} style={{ background: '#ffffff', border: '1px solid #e2e8f0', color: '#334155', fontSize: '11px', padding: '2px 6px' }}>
                                      {getCategoryName(categoryId)}
                                    </PreferenceTag>
                                  ))}
                                </>
                              )}
                              
                              {preference.keywords && preference.keywords.length > 0 && (
                                <>
                                  {preference.categories && preference.categories.length > 0 && (
                                    <span style={{ fontSize: '10px', color: '#64748b', margin: '0 2px' }}>+</span>
                                  )}
                                  {preference.keywords.map((keyword, index) => (
                                    <PreferenceTag key={`keyword-${index}`} style={{ background: '#ffffff', border: '1px solid #e2e8f0', color: '#334155', fontSize: '11px', padding: '2px 6px' }}>
                                      {keyword}
                                    </PreferenceTag>
                                  ))}
                                </>
                              )}
                            </div>
                          </div>

                          {/* Compact Settings Row */}
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            fontSize: "11px",
                            color: "#6b7280"
                          }}>
                            <span>Max {preference.max_notifications_per_day}/day</span>
                            <span style={{ 
                              padding: "2px 6px",
                              borderRadius: "3px",
                              background: preference.is_active ? "#dcfce7" : "#fee2e2",
                              color: preference.is_active ? "#166534" : "#dc2626",
                              fontWeight: "500",
                              fontSize: "10px"
                            }}>
                              {preference.is_active ? "Active" : "Inactive"}
                            </span>
                          </div>

                          {/* Advanced Settings (if any) */}
                          {(preference.description || preference.task_type || preference.min_budget || preference.max_budget) && (
                            <details style={{ marginTop: "6px" }}>
                              <summary style={{ 
                                fontSize: "10px", 
                                color: "#9ca3af", 
                                cursor: "pointer"
                              }}>
                                Advanced
                              </summary>
                              <div style={{ paddingTop: "4px", fontSize: "10px", color: "#9ca3af" }}>
                                {preference.description && <div>{preference.description}</div>}
                                {preference.task_type && <div>Type: {preference.task_type}</div>}
                                {(preference.min_budget || preference.max_budget) && (
                                  <div>Budget: ${preference.min_budget || 0}-${preference.max_budget || "∞"}</div>
                                )}
                              </div>
                            </details>
                          )}
                        </PreferenceDetails>
                      </PreferenceCard>
                    ))}
                  </PreferencesGrid>
                )}
                
                <SaveButton onClick={() => openPreferenceModal()}>
                  <Plus size={16} style={{ marginRight: "8px" }} />
                  Add New Preference
                </SaveButton>
              </Card>
            </CardsWrapper>
          )}
          
          {/* Preference Modal */}
          {showPreferenceModal && (
            <ModalOverlay onClick={closePreferenceModal}>
              <Modal onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                  <ModalTitle>
                    {editingPreference ? "Edit" : "Create"} Notification Preference
                  </ModalTitle>
                  <ActionButton onClick={closePreferenceModal}>
                    <X size={16} />
                  </ActionButton>
                </ModalHeader>
                
                <ModalContent>
                  <FormRow>
                  <Label>Preference Name <RequiredAsterisk>*</RequiredAsterisk></Label>
                  <Input
                    value={preferenceForm.name}
                    onChange={(e) => handlePreferenceFormChange("name", e.target.value)}
                    placeholder="e.g., Urgent Local Tasks"
                  />
                </FormRow>
                
                <FormRow>
                  <Label>Location</Label>
                  <Select
                    value={preferenceForm.location_type}
                    onChange={(e) => handlePreferenceFormChange("location_type", e.target.value)}
                  >
                    {getLocationOptions(preferenceForm.task_type).map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormRow>
                
                {preferenceForm.location_type === "zipcode" && (
                  <div>
                    <FormRow>
                      <Label>ZIP Code</Label>
                      <Input
                        type="text"
                        value={preferenceForm.zipcode}
                        onChange={(e) => handlePreferenceFormChange("zipcode", e.target.value)}
                        placeholder="90210"
                      />
                    </FormRow>
                    
                    <FormRow>
                      <Label>Radius</Label>
                      <Select
                        value={preferenceForm.radius}
                        onChange={(e) => handlePreferenceFormChange("radius", e.target.value)}
                      >
                        <option value="5">5 miles</option>
                        <option value="10">10 miles</option>
                        <option value="25">25 miles</option>
                        <option value="50">50 miles</option>
                        <option value="100">100 miles</option>
                      </Select>
                    </FormRow>
                  </div>
                )}
                
                {/* Matching Criteria Section */}
                <div style={{ 
                  background: '#ffffff', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '10px', 
                  padding: '12px',
                  margin: '12px 0'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    marginBottom: '10px',
                    fontWeight: '600',
                    fontSize: '13px',
                    color: '#334155'
                  }}>
                    What tasks should match this alert?
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        type="button"
                        onMouseEnter={(e) => {
                          const tooltip = e.currentTarget.nextElementSibling;
                          if (tooltip) tooltip.style.display = 'block';
                        }}
                        onMouseLeave={(e) => {
                          const tooltip = e.currentTarget.nextElementSibling;
                          if (tooltip) tooltip.style.display = 'none';
                        }}
                        style={{
                          background: '#f1f5f9',
                          color: '#64748b',
                          border: '1px solid #e2e8f0',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ?
                      </button>
                      <div style={{
                        display: 'none',
                        position: 'absolute',
                        top: '30px',
                        left: '-180px',
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '12.5px',
                        color: '#475569',
                        width: '300px',
                        zIndex: 1001,
                        boxShadow: '0 8px 20px rgba(15, 23, 42, 0.12)',
                        pointerEvents: 'none'
                      }}>
                        <div style={{ fontWeight: '600', marginBottom: '6px' }}>Example:</div>
                        If you select "Handyman" category + "paint" keyword, you'll get:
                        <ul style={{ margin: '8px 0 0 16px', paddingLeft: '0' }}>
                          <li>✅ ALL handyman tasks (furniture assembly, mounting, repairs, etc.)</li>
                          <li>✅ AND ALSO any task mentioning "paint" (even from other categories)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  

                  <FormRow>
                    <Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Categories <span style={{ fontSize: '11.5px', color: '#64748b', fontWeight: 'normal' }}>(Select any that interest you)</span>
                    </Label>
                    <CheckboxGroup>
                      {categories.length > 0 ? (
                        categories.map((category) => (
                          <CheckboxLabel key={category.id}>
                            <input
                              type="checkbox"
                              checked={preferenceForm.categories.includes(category.id)}
                              onChange={() => toggleCategory(category.id)}
                            />
                            {category.name}
                          </CheckboxLabel>
                        ))
                      ) : (
                        <div style={{ padding: "12px", color: "#64748b", fontStyle: "italic" }}>
                          Loading categories...
                        </div>
                      )}
                    </CheckboxGroup>
                  </FormRow>

                  {/* Plus Keywords Section */}
                  <div style={{ 
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '10px',
                    margin: '12px 0',
                    fontSize: '12px',
                    color: '#334155',
                    textAlign: 'center'
                  }}>
                    <strong>You can also get notified for keywords</strong>
                  </div>

                  <FormRow>
                    <Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Keywords <span style={{ fontSize: '11.5px', color: '#64748b', fontWeight: 'normal' }}>(Words in task titles/descriptions)</span>
                    </Label>
                    {/* Existing keywords */}
                    {preferenceForm.keywords.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                        {preferenceForm.keywords.map((keyword, index) => (
                          <KeywordTag key={index}>
                            {keyword}
                            <button 
                              type="button"
                              onClick={() => removeKeyword(keyword)}
                              aria-label={`Remove ${keyword}`}
                            >
                              ×
                            </button>
                          </KeywordTag>
                        ))}
                      </div>
                    )}
                    
                    {/* Add keyword input */}
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <Input
                        type="text"
                        placeholder="e.g. doorbell, garage door, drywall..."
                        value={currentKeyword}
                        onChange={(e) => {
                          setCurrentKeyword(e.target.value);
                          setKeywordError(''); // Clear error when typing
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (currentKeyword.trim()) {
                              addKeyword(currentKeyword);
                            }
                          }
                        }}
                        style={{ 
                          flex: 1,
                          borderColor: keywordError ? '#dc2626' : undefined
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (currentKeyword.trim()) {
                            addKeyword(currentKeyword);
                          }
                        }}
                        style={{
                          background: '#3b82f6',
                          color: 'white',
                          border: '1px solid #3b82f6',
                          borderRadius: '6px',
                          padding: '7px 10px',
                          fontSize: '12.5px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        + Add
                      </button>
                    </div>
                    
                    {/* Error message */}
                    {keywordError && (
                      <div style={{ 
                        fontSize: '11.5px', 
                        color: '#dc2626', 
                        marginTop: '6px',
                        fontWeight: '500',
                        background: '#fff7f7',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        padding: '6px 10px'
                      }}>
                        {keywordError}
                      </div>
                    )}
                    
                    <div style={{ 
                      fontSize: '11.5px', 
                      color: '#64748b', 
                      marginTop: '6px',
                      fontWeight: '500'
                    }}>
                      Tip: Keep keywords 1 or 2 words maximum for better matching
                    </div>
                  </FormRow>
                </div>
                
                {/* Dynamic Preview Text */}
                <div style={{
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '12px',
                  margin: '12px 0',
                  fontSize: '12.5px',
                  color: '#334155',
                  lineHeight: '1.5'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Preview
                  </div>
                  <div style={{ fontStyle: 'italic' }}>
                    {previewText}
                  </div>
                </div>

                {/* Advanced Options Toggle */}
                <div style={{ margin: '12px 0' }}>
                  <button
                    type="button"
                    onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#3b82f6',
                      fontSize: '12.5px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 0'
                    }}
                  >
                    {showAdvancedOptions ? '▼' : '▶'} Advanced Options
                  </button>
                </div>

                {showAdvancedOptions && (
                  <div style={{ 
                    background: '#ffffff', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px', 
                    padding: '12px',
                    margin: '12px 0'
                  }}>
                    <FormRow>
                      <Label>Description</Label>
                      <TextArea
                        value={preferenceForm.description}
                        onChange={(e) => handlePreferenceFormChange("description", e.target.value)}
                        placeholder="Optional description of what you want to be notified about"
                        rows={2}
                      />
                    </FormRow>
                    
                    <FormRow>
                      <Label>Task Type</Label>
                      <Select
                        value={preferenceForm.task_type}
                        onChange={(e) => handlePreferenceFormChange("task_type", e.target.value)}
                      >
                        <option value="">All tasks</option>
                        <option value="online">Remote</option>
                        <option value="in-person">In-person</option>
                      </Select>
                    </FormRow>
                    
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                      <FormRow>
                        <Label>Min Budget ($)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={preferenceForm.min_budget}
                          onChange={(e) => handlePreferenceFormChange("min_budget", e.target.value)}
                          placeholder="0"
                        />
                      </FormRow>
                      
                      <FormRow>
                        <Label>Max Budget ($)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={preferenceForm.max_budget}
                          onChange={(e) => handlePreferenceFormChange("max_budget", e.target.value)}
                          placeholder="No limit"
                        />
                      </FormRow>
                    </div>
                    
                    <FormRow>
                      <Label>Max notifications/day</Label>
                      <Input
                        type="number"
                        min="1"
                        max="50"
                        value={preferenceForm.max_notifications_per_day}
                        onChange={(e) => handlePreferenceFormChange("max_notifications_per_day", e.target.value)}
                      />
                    </FormRow>
                  </div>
                )}
                
                
                
                </ModalContent>
                
                <ModalFooter>
                  <ButtonGroup>
                    <ModalButton 
                      onClick={closePreferenceModal}
                      disabled={savingPreference}
                    >
                      Cancel
                    </ModalButton>
                    <ModalButton 
                      primary 
                      onClick={savePreference}
                      disabled={!preferenceForm.name.trim() || savingPreference}
                    >
                      {savingPreference 
                        ? "Saving..." 
                        : `${editingPreference ? "Update" : "Create"} Preference`
                      }
                    </ModalButton>
                  </ButtonGroup>
                </ModalFooter>
              </Modal>
            </ModalOverlay>
          )}

        </OuterContainer>
      </PageWrapper>
    </>
  );
};

export default connect((state) => ({ state }))(AccountSettings);
